import { useContent } from "contexts/contentContext/contentContext";
import Button from "components/Elements/Button/Button";
import Image from "components/Elements/Image/Image";
import { useState, useEffect, useRef, useCallback } from "react";

import styles from "./reach.module.scss";

interface ReachI {
  handleScrollToForm: () => void;
}

const Reach = ({ handleScrollToForm }: ReachI) => {
  const { getTextByKey } = useContent();
  const [showVideo, setShowVideo] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        // If the section is visible at 50% or more, start the video
        if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
          setShowVideo(true);
          observer.disconnect(); // Disable observer after video starts
        }
      },
      {
        threshold: 0.5, // Launch when 50% of the section is visible
        rootMargin: "0px",
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  const handlePlayClick = useCallback(() => setShowVideo(true), []);

  return (
    <div className={styles.reachSection} ref={sectionRef}>
      <div className={styles.reachVideoContent}>
        <div className={styles.videoWrapper}>
          {showVideo ? (
            <iframe
              width="100%"
              height="100%"
              src="https://www.youtube.com/embed/bXReDWuc97c?autoplay=1&mute=1"
              title="YouTube video player"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          ) : (
            <div
              className={styles.thumbnailWrapper}
              onClick={handlePlayClick}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") handlePlayClick();
              }}
              tabIndex={0}
              role="button"
              aria-label={getTextByKey("play-video")}
            >
              <Image
                name="advertising-page-video-preview"
                ext="png"
                className={styles.thumbnailImage}
              />
              <div className={styles.playButton}>
                <Image name="advertising-page-play-icon" ext="png" />
              </div>
            </div>
          )}
        </div>
      </div>
      <div className={styles.reachInfo}>
        <div className={styles.reachInfoContent}>
          <h2>{getTextByKey("tailored-for-advertisers")}</h2>
          <p>{getTextByKey("reach-description")}</p>
          <Button className={styles.reachButton} onClick={handleScrollToForm}>
            {getTextByKey("start-your-campaign")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Reach;
